import * as yup from 'yup';
import { isBefore, startOfDay } from 'date-fns';
import { staticDataHelper } from '../../../config';
import { ALLOWED_PASSPORT_NUMBER_VALIDATION_MESSAGE } from '../../../constants';

const minAge = 18;
export const maxDateofBirth = new Date();
maxDateofBirth.setUTCFullYear(new Date().getFullYear() - minAge);

const maximumAcceptableExpiredYearsPassport = 3;
const maximumAcceptableExpiredYearDriverLicence = 2;

const getExpiryDate = (maximumAcceptableExpiredYears) => {
  const earliestExpiry = new Date();
  earliestExpiry.setUTCFullYear(new Date().getFullYear() - maximumAcceptableExpiredYears);
  return earliestExpiry;
};

const australianPassportRegExp = /^[A-Za-z]{1,2}\d{7}$/;

const isAustralianPassportNumberValid = (value) => {
  if (australianPassportRegExp.test(value)) {
    return true;
  }
  return false;
};

export const earliestPassportExpiry = getExpiryDate(maximumAcceptableExpiredYearsPassport);
export const earliestDriverLicenceExpiry = getExpiryDate(maximumAcceptableExpiredYearDriverLicence);

export const validationSchema = yup.object().shape({
  firstName: yup.mixed().test({
    name: 'firstNameRequired',
    test(value) {
      const selectedDocumentType = this.resolve(yup.ref('documentType'));

      if (!value && staticDataHelper.isDocumentTypeDriversLicence(selectedDocumentType)) {
        return this.createError({
          message: 'Please enter a first name',
          path: 'firstName',
        });
      }

      if (!value && staticDataHelper.isDocumentTypePassport(selectedDocumentType)) {
        return this.createError({
          message: 'Please enter given names',
          path: 'firstName',
        });
      }

      if (value && value.length > 40) {
        return this.createError({
          message: 'Please enter no more than 40 characters',
          path: 'firstName',
        });
      }

      return true;
    },
  }),
  lastName: yup
    .string()
    .max(80, 'Please enter no more than 80 characters')
    .required('Please enter a last name'),
  email: yup.string().email('Please enter a valid email').required('Please enter an email'),
  birthDate: yup
    .date()
    .typeError('Please enter a valid date of birth')
    .max(maxDateofBirth, `You must be at least ${minAge} years old`)
    .required('Please enter a valid date of birth'),
  documentType: yup.string().required('Please select how you want to be verified'),
  driversLicenseNumber: yup.string().when('documentType', {
    is: (documentType) => staticDataHelper.isDocumentTypeDriversLicence(documentType),
    then: yup
      .string()
      .max(10, 'Please enter a valid licence number')
      .required('Please enter a driver licence number'),
  }),
  driversLicenceCardNumber: yup.string().when('documentType', {
    is: (documentType) => staticDataHelper.isDocumentTypeDriversLicence(documentType),
    then: yup
      .string()
      .min(6, 'Please enter a valid card number')
      .max(10, 'Please enter a valid card number')
      .required('Please enter a driver licence card number'),
  }),
  driversLicenceState: yup
    .mixed()
    .test({
      name: 'driversLicenceStateRequired',
      test(value) {
        const selectedDocumentType = this.resolve(yup.ref('documentType'));

        if (!value && staticDataHelper.isDocumentTypeDriversLicence(selectedDocumentType)) {
          return this.createError({
            message: 'Please select state of issue',
            path: 'driversLicenceState',
          });
        }

        return true;
      },
    })
    .nullable(),
  passportNumber: yup.string().when('documentType', {
    is: (documentType) => staticDataHelper.isDocumentTypePassport(documentType),
    then: yup
      .string()
      .min(8, 'Please enter a valid passport number')
      .max(9, 'Please enter a valid passport number')
      .required('Please enter a passport number')
      .test('passportNumber', ALLOWED_PASSPORT_NUMBER_VALIDATION_MESSAGE, (value) =>
        isAustralianPassportNumberValid(value),
      ),
  }),
  documentExpirationDate: yup
    .date()
    .typeError('Please enter a valid expiry date')
    .test({
      name: 'documentExpirationDate',
      test(value) {
        if (!value) {
          return this.createError({
            message: 'Please enter a valid expiry date',
            path: 'documentExpirationDate',
          });
        }

        const selectedDocumentType = this.resolve(yup.ref('documentType'));
        const isDocumentDriversLicence =
          staticDataHelper.isDocumentTypeDriversLicence(selectedDocumentType);
        if (
          isDocumentDriversLicence &&
          value &&
          isBefore(startOfDay(new Date(value)), startOfDay(earliestDriverLicenceExpiry))
        ) {
          return this.createError({
            message: 'Invalid expiry date - please provide a more recent document',
            path: 'documentExpirationDate',
          });
        }

        const isDocumentTypePassport =
          staticDataHelper.isDocumentTypePassport(selectedDocumentType);
        if (isDocumentTypePassport && value && new Date(value) < earliestPassportExpiry) {
          return this.createError({
            message: 'Invalid expiry date - please provide a more recent document',
            path: 'documentExpirationDate',
          });
        }
        return true;
      },
    })
    .required('Please enter an expiry date'),
  consent: yup.bool().test('consent', 'Do you agree?', (x) => x),
  homeAddress: yup.string().required('Please enter a home address').nullable(),
});
